// LiveQuotationFromTemplate.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  Edit,
  Delete,
  Add,
  Remove,
  AttachMoney,
  Person,
  Business,
  Description,
  LocalShipping,
  Receipt,
  ShoppingCart,
  Title,
  CloudUpload,
  Download,
  Print,
  PictureAsPdf,
  ContentCopy,
  CheckCircle,
  Error,
  Refresh,
  Search,
  AutoFixHigh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const LiveQuotationFromTemplate = () => {
  const navigate = useNavigate();
  
  // State for template data from localStorage
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for editable quotation data
  const [quotation, setQuotation] = useState({
    id: Date.now().toString(),
    templateId: '',
    companyDetails: {},
    clientDetails: {},
    quotationDetails: {},
    items: [],
    additionalCosts: [],
    summary: {},
    terms: [],
    status: 'draft',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  
  // State for clients and services for auto-complete
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  
  // State for UI
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);
  const [companyConfig, setCompanyConfig] = useState(null);
  
  // Load template from localStorage on component mount
  useEffect(() => {
    const loadTemplate = () => {
      try {
        const savedTemplate = localStorage.getItem('currentTemplate');
        
        if (!savedTemplate) {
          setError('No template found. Please design a template first.');
          setLoading(false);
          return;
        }
        
        const parsedTemplate = JSON.parse(savedTemplate);
        setTemplate(parsedTemplate);
        
        // Initialize quotation data from template
        initializeQuotationData(parsedTemplate);
        
        setLoading(false);
      } catch (err) {
        //console.error('Error loading template:', err);
        setError('Failed to load template. Please try again.');
        setLoading(false);
      }
    };
    
    loadTemplate();
    
    // Fetch clients and services for auto-complete
    fetchClients();
    fetchServices();

    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        if (company_id) {
          const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cmpRes.data?.data) setCompanyConfig(cmpRes.data.data);
        }
      } catch (e) {}
    };
    fetchConfig();
  }, []);
  
  // Initialize quotation data from template
  const initializeQuotationData = (templateData) => {
    const newQuotation = {
      ...quotation,
      templateId: templateData.id,
      templateName: templateData.name,
      
      // Company details from template header
      companyDetails: {
        name: templateData.header.company_name_right,
        details: templateData.header.company_details_left.reduce((acc, detail) => {
          acc[detail.label.replace(':', '').toLowerCase()] = detail.content;
          return acc;
        }, {}),
        headerColor: templateData.header.header_color,
      },
   // Advance payment settings from template
    advancePercentage: templateData.summary_section?.advance_payment_options?.advance_payment?.percentage || 50,
    selectedPaymentOption: templateData.summary_section?.advance_payment_options?.full_payment?.enabled ? 'full' : 
                          templateData.summary_section?.advance_payment_options?.advance_payment?.enabled ? 'advance' : 'custom',
    discountPercent: templateData.summary_section?.default_discount_percent || 0,
    
    // Bank details from template
    bankDetails: templateData.summary_section?.bank_details || {
      bank_name: '',
      account_number: '',
      account_name: '',
      ifsc_code: '',
      branch: ''
    },
    
      // Client details (empty initially)
      clientDetails: {
        name: templateData.details_section.bill_to.client_name,
        address: templateData.details_section.bill_to.client_address,
        contact: templateData.details_section.bill_to.client_contact,
        email: templateData.details_section.bill_to.client_email,
        gst: templateData.details_section.bill_to.client_gst,
      },
      
      // Quotation details from template
      quotationDetails: templateData.details_section.quotation_details.rows.reduce((acc, row) => {
        const key = row.label.replace(':', '').replace(/\s+/g, '_').toLowerCase();
        acc[key] = row.content;
        return acc;
      }, {}),
      
      // Items from template table
      items: templateData.items_table.rows.map(item => ({
        id: item.id,
        description: item.description,
        unit: item.unit,
        unitPrice: item.unit_price,
        quantity: item.default_qty,
        cgst: item.cgst,
        sgst: item.sgst,
        remarks: item.remarks,
        required: item.required,
      })),
      
      // Additional costs from template
      additionalCosts: templateData.additional_costs.costs.map(cost => ({
        id: cost.id,
        name: cost.name,
        amount: cost.default_value,
        enabled: cost.enabled_by_default,
        description: cost.description,
        required: cost.required,
      })),
      
      // Terms from template
      terms: templateData.terms_section.items.map(term => ({
        id: term.id,
        label: term.label,
        content: term.content,
        visible: term.visible,
      })),
    };
    
    setQuotation(newQuotation);
  };
  
  // Fetch clients for auto-complete
  const fetchClients = async () => {
    setClientsLoading(true);
    try {
          const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/quotation-import/clients`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        withCredentials: true,
      });
      setClients(response.data);
    } catch (error) {
      //console.error('Error fetching clients:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load clients',
        severity: 'error'
      });
    } finally {
      setClientsLoading(false);
    }
  };
  
  // Fetch services for auto-complete
  const fetchServices = async () => {
    setServicesLoading(true);
      const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
    try {
      const response = await axios.get(`${API_URL}/api/quotation-import/services`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        withCredentials: true,
      });
      setServices(response.data);
    } catch (error) {
      //console.error('Error fetching services:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load services',
        severity: 'error'
      });
    } finally {
      setServicesLoading(false);
    }
  };
  
  // Handle client auto-complete selection
  const handleClientSelect = (event, value) => {
    if (value) {
      setQuotation(prev => ({
        ...prev,
        clientDetails: {
          name: value.name,
          address: value.address,
          contact: value.contact,
          email: value.email,
          gst: value.gst,
        }
      }));
      
      setSnackbar({
        open: true,
        message: `Client "${value.name}" loaded`,
        severity: 'success'
      });
    }
  };
  
  // Handle service auto-complete selection for items
  const handleServiceSelect = (index, value) => {
    if (value) {
      const updatedItems = [...quotation.items];
      updatedItems[index] = {
        ...updatedItems[index],
        description: value.description,
        unit: value.unit,
        unitPrice: value.unit_price,
      };
      
      setQuotation(prev => ({
        ...prev,
        items: updatedItems
      }));
      
      setSnackbar({
        open: true,
        message: `Service "${value.description}" loaded`,
        severity: 'success'
      });
    }
  };
  
  // Handle item field change
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...quotation.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setQuotation(prev => ({ ...prev, items: updatedItems }));
  };
  
  // Handle additional cost change
  const handleAdditionalCostChange = (index, field, value) => {
    const updatedCosts = [...quotation.additionalCosts];
    updatedCosts[index] = { ...updatedCosts[index], [field]: value };
    setQuotation(prev => ({ ...prev, additionalCosts: updatedCosts }));
  };
  
  // Add new item row
  const handleAddItem = () => {
    const newItem = {
      id: `item_${Date.now()}`,
      description: '',
      unit: 'pc',
      unitPrice: 0,
      quantity: 1,
      cgst: 9,
      sgst: 9,
      remarks: '',
      required: false,
    };
    
    setQuotation(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };
  
  // Remove item row
  const handleRemoveItem = (index) => {
    const updatedItems = quotation.items.filter((_, i) => i !== index);
    setQuotation(prev => ({ ...prev, items: updatedItems }));
  };
  
  // Calculate totals

const calculateTotals = () => {
  let itemsTotal = 0;
  let cgstTotal = 0;
  let sgstTotal = 0;
  
  quotation.items.forEach(item => {
    const itemSubtotal = (item.quantity || 1) * (item.unitPrice || 0);
    itemsTotal += itemSubtotal;
    
    const itemCGST = itemSubtotal * ((item.cgst || 0) / 100);
    const itemSGST = itemSubtotal * ((item.sgst || 0) / 100);
    cgstTotal += itemCGST;
    sgstTotal += itemSGST;
  });
  
  const additionalCostsTotal = quotation.additionalCosts
    .filter(cost => cost.enabled)
    .reduce((sum, cost) => sum + (parseFloat(cost.amount) || 0), 0);
  
  const subtotal = itemsTotal + additionalCostsTotal;
  
  // DISCOUNT CALCULATION
  const discountPercent = quotation.discountPercent || template?.summary_section?.default_discount_percent || 0;
  const discountAmount = subtotal * (discountPercent / 100);
  const afterDiscount = subtotal - discountAmount;
  
  const gstTotal = cgstTotal + sgstTotal;
  const grandTotal = afterDiscount + gstTotal;
  
  // ADVANCE PAYMENT CALCULATION
  const advancePercentage = quotation.advancePercentage || 
                          template?.summary_section?.advance_payment_options?.advance_payment?.percentage || 
                          50;
  const advanceAmount = grandTotal * (advancePercentage / 100);
  const remainingAmount = grandTotal - advanceAmount;
  
  return {
    itemsTotal,
    additionalCostsTotal,
    subtotal,
    discountPercent,
    discountAmount,
    afterDiscount,
    cgstTotal,
    sgstTotal,
    gstTotal,
    grandTotal,
    advancePercentage,
    advanceAmount,
    remainingAmount
  };
};
  
  const totals = calculateTotals();
  // Handle payment option selection
const handlePaymentOptionChange = (option) => {
  setQuotation(prev => ({
    ...prev,
    selectedPaymentOption: option
  }));
};

// Handle advance percentage change
const handleAdvancePercentageChange = (value) => {
  setQuotation(prev => ({
    ...prev,
    advancePercentage: parseFloat(value)
  }));
};

// Handle bank detail change
const handleBankDetailChange = (field, value) => {
  setQuotation(prev => ({
    ...prev,
    bankDetails: {
      ...prev.bankDetails,
      [field]: value
    }
  }));
};
  // Format currency
  const formatCurrency = (amount) => {
    return `${companyConfig?.currency_symbol || '₹'}${(amount || 0).toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };
  
  // Save quotation
const handleSaveQuotation = async () => {
  setSaving(true);
  
  try {
    // Prepare quotation data for API
    const quotationToSave = {
      ...quotation,
      totals: calculateTotals(),
      paymentDetails: {
        selectedOption: quotation.selectedPaymentOption,
        advancePercentage: quotation.advancePercentage,
        advanceAmount: totals.advanceAmount,
        remainingAmount: totals.remainingAmount,
        bankDetails: quotation.bankDetails
      },
      updatedAt: new Date().toISOString(),
    };
    
    // Get token and company_id
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    // Save to API
    const response = await axios.post(
      `${API_URL}/api/qt-template`,
      quotationToSave,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        withCredentials: true
      
      }
    );
    
    setSnackbar({
      open: true,
      message: 'Quotation saved successfully! Redirecting to preview...',
      severity: 'success'
    });
    // Update quotation with server ID if needed
    if (response.data.id) {
      setQuotation(prev => ({ ...prev, id: response.data.id }));
      
      // Navigate to preview page after a short delay
      setTimeout(() => {
        navigate(`/quotation-preview/${response.data.id}`);
      }, 1500);
    }
    
  } catch (error) {
    //console.error('Error saving quotation:', error);
    setSnackbar({
      open: true,
      message: error.response?.data?.message || 'Failed to save quotation',
      severity: 'error'
    });
  } finally {
    setSaving(false);
  }
};

  

  
  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading template...
        </Typography>
      </Container>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/template-designer')}
        >
          Go to Template Designer
        </Button>
      </Container>
    );
  }
  
  // Main render
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/template-designer')}
            >
              Back to Designer
            </Button>
            <Typography variant="h4" fontWeight="bold">
              Create Quotation from Template
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
          
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSaveQuotation}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Quotation'}
            </Button>
          </Box>
        </Box>
        
        <Typography variant="subtitle1" color="text.secondary">
          Template: <strong>{template.name}</strong> | All fields are editable
        </Typography>
      </Box>
      
      {/* Main Quotation Form */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          border: 1, 
          borderColor: 'divider',
          position: 'relative'
        }}
      >
   {/* Header Section */}
<Box 
  sx={{ 
    p: 3, 
    mb: 4, 
    bgcolor: template?.header?.header_color || '#1976d2',
    color: 'white',
    borderRadius: 1,
  }}
>
  <Grid container spacing={3}>
    {/* Left Side - Company Details */}
    <Grid item xs={12} md={6}>

      
      {/* Company Name */}
      <TextField
        fullWidth
        label="Company Name"
        value={quotation.companyDetails.name}
        onChange={(e) => setQuotation(prev => ({
          ...prev,
          companyDetails: { ...prev.companyDetails, name: e.target.value }
        }))}
        sx={{ mb: 2, bgcolor: 'white', borderRadius: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Business />
            </InputAdornment>
          ),
        }}
      />
      
      {/* Left Side Fields - Address */}
      {template?.header?.company_details_left
        .filter(detail => detail.visible)
        .map((detail, index) => {
          const label = detail.label.toLowerCase();
          // Only show address-related fields on left side
          if (label.includes('address') || label.includes('location') || label.includes('street')) {
            return (
              <TextField
                key={detail.id}
                fullWidth
                label={detail.label}
                multiline={label.includes('address')}
                rows={label.includes('address') ? 2 : 1}
                value={quotation.companyDetails.details[detail.label.replace(':', '').toLowerCase()] || ''}
                onChange={(e) => {
                  const key = detail.label.replace(':', '').toLowerCase();
                  const updatedDetails = { ...quotation.companyDetails.details, [key]: e.target.value };
                  setQuotation(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, details: updatedDetails }
                  }));
                }}
                sx={{ mb: 1, bgcolor: 'white', borderRadius: 1 }}
              />
            );
          }
          return null;
        })}
    </Grid>
    
    {/* Right Side - Contact Information */}
    <Grid item xs={12} md={6}>
  
      
      {/* Right Side Fields - Email, Contact, GST */}
      {template?.header?.company_details_left
        .filter(detail => detail.visible)
        .map((detail, index) => {
          const label = detail.label.toLowerCase();
          // Show email, contact, phone, gst, vat on right side
          if (label.includes('email') || label.includes('contact') || label.includes('phone') || 
              label.includes('gst') || label.includes('vat') || label.includes('tax')) {
            return (
              <TextField
                key={detail.id}
                fullWidth
                label={detail.label}
                value={quotation.companyDetails.details[detail.label.replace(':', '').toLowerCase()] || ''}
                onChange={(e) => {
                  const key = detail.label.replace(':', '').toLowerCase();
                  const updatedDetails = { ...quotation.companyDetails.details, [key]: e.target.value };
                  setQuotation(prev => ({
                    ...prev,
                    companyDetails: { ...prev.companyDetails, details: updatedDetails }
                  }));
                }}
                sx={{ mb: 1, bgcolor: 'white', borderRadius: 1 }}
                InputProps={{
                  startAdornment: label.includes('email') ? (
                    <InputAdornment position="start">
                      <Business />
                    </InputAdornment>
                  ) : null,
                }}
              />
            );
          }
          return null;
        })}
      
      {/* If no contact fields found in template, show default ones */}
      {template?.header?.company_details_left.filter(detail => {
        const label = detail.label.toLowerCase();
        return label.includes('email') || label.includes('contact') || label.includes('phone') || 
               label.includes('gst') || label.includes('vat') || label.includes('tax');
      }).length === 0 && (
        <>
          <TextField
            fullWidth
            label="Email"
            value={quotation.companyDetails.details.email || ''}
            onChange={(e) => {
              const updatedDetails = { ...quotation.companyDetails.details, email: e.target.value };
              setQuotation(prev => ({
                ...prev,
                companyDetails: { ...prev.companyDetails, details: updatedDetails }
              }));
            }}
            sx={{ mb: 1, bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            fullWidth
            label="Contact"
            value={quotation.companyDetails.details.contact || ''}
            onChange={(e) => {
              const updatedDetails = { ...quotation.companyDetails.details, contact: e.target.value };
              setQuotation(prev => ({
                ...prev,
                companyDetails: { ...prev.companyDetails, details: updatedDetails }
              }));
            }}
            sx={{ mb: 1, bgcolor: 'white', borderRadius: 1 }}
          />
          
          <TextField
            fullWidth
            label="GST Number"
            value={quotation.companyDetails.details.gst || ''}
            onChange={(e) => {
              const updatedDetails = { ...quotation.companyDetails.details, gst: e.target.value };
              setQuotation(prev => ({
                ...prev,
                companyDetails: { ...prev.companyDetails, details: updatedDetails }
              }));
            }}
            sx={{ mb: 1, bgcolor: 'white', borderRadius: 1 }}
          />
        </>
      )}
    </Grid>
  </Grid>
</Box>
        
        {/* Bill To & Quotation Details Section */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Left Side - Bill To */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person /> Bill To
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={clients}
                loading={clientsLoading}
                getOptionLabel={(option) => option.name}
                onChange={handleClientSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Client"
                    placeholder="Type client name..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Client Name"
              value={quotation.clientDetails.name}
              onChange={(e) => setQuotation(prev => ({
                ...prev,
                clientDetails: { ...prev.clientDetails, name: e.target.value }
              }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={quotation.clientDetails.address}
              onChange={(e) => setQuotation(prev => ({
                ...prev,
                clientDetails: { ...prev.clientDetails, address: e.target.value }
              }))}
              sx={{ mb: 2 }}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact"
                  value={quotation.clientDetails.contact}
                  onChange={(e) => setQuotation(prev => ({
                    ...prev,
                    clientDetails: { ...prev.clientDetails, contact: e.target.value }
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={quotation.clientDetails.email}
                  onChange={(e) => setQuotation(prev => ({
                    ...prev,
                    clientDetails: { ...prev.clientDetails, email: e.target.value }
                  }))}
                />
              </Grid>
            </Grid>
            
            <TextField
              fullWidth
              label="GST/VAT Number"
              value={quotation.clientDetails.gst}
              onChange={(e) => setQuotation(prev => ({
                ...prev,
                clientDetails: { ...prev.clientDetails, gst: e.target.value }
              }))}
              sx={{ mt: 2 }}
            />
          </Grid>
          
          {/* Right Side - Quotation Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt /> Quotation Details
            </Typography>
            
            {Object.entries(quotation.quotationDetails).map(([key, value]) => (
              <TextField
                key={key}
                fullWidth
                label={key.replace('_', ' ').toUpperCase()}
                value={value}
                onChange={(e) => setQuotation(prev => ({
                  ...prev,
                  quotationDetails: { ...prev.quotationDetails, [key]: e.target.value }
                }))}
                sx={{ mb: 2 }}
              />
            ))}
          </Grid>
        </Grid>
        
     {/* Items Table Section */}
{template?.items_table?.enabled && (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <ShoppingCart /> {template.items_table.title || 'Items & Services'}
    </Typography>
    
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: template?.items_table?.header_color || '#1976d2' }}>
            {/* Render ONLY visible columns from template */}
            {template.items_table.columns
              .filter(col => col.visible)
              .map(col => (
                <TableCell 
                  key={col.id}
                  align={
                    col.name.includes('Price') || 
                    col.name.includes('Amt') || 
                    col.name.includes('Subtotal') ? 'right' : 
                    col.name === 'Unit' || col.name === 'Qty' || col.name.includes('%') ? 'center' : 'left'
                  }
                  sx={{ color: 'white', fontWeight: 'bold', width: `${col.width}%` }}
                >
                  {col.name}
                </TableCell>
              ))}
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {quotation.items.map((item, index) => (
            <TableRow key={item.id}>
              {/* Column 1: # (Auto-number) */}
              {template.items_table.columns.find(col => col.name === '#' && col.visible) && (
                <TableCell>{index + 1}</TableCell>
              )}
              
              {/* Column 2: Description with auto-complete */}
              {template.items_table.columns.find(col => col.name === 'Description' && col.visible) && (
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Autocomplete
                      freeSolo
                      options={services}
                      loading={servicesLoading}
                      getOptionLabel={(option) => option.description || ''}
                      onChange={(event, value) => handleServiceSelect(index, value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Type service description..."
                          fullWidth
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                    <IconButton size="small" title="Search services">
                      {/* <Search /> */}
                    </IconButton>
                  </Box>
                </TableCell>
              )}
              
              {/* Column 3: Unit */}
              {template.items_table.columns.find(col => col.name === 'Unit' && col.visible) && (
                <TableCell align="center">
                  <TextField
                    size="small"
                    value={item.unit}
                    onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                    sx={{ width: 80 }}
                  />
                </TableCell>
              )}
              
              {/* Column 4: Unit Price */}
              {template.items_table.columns.find(col => col.name === 'Unit Price' && col.visible) && (
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value))}
                    sx={{ width: 100 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* <AttachMoney fontSize="small" /> */}
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
              )}
              
              {/* Column 5: Quantity */}
              {template.items_table.columns.find(col => col.name === 'Qty' && col.visible) && (
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                    sx={{ width: 70 }}
                  />
                </TableCell>
              )}
              
              {/* Column 6: Subtotal (Calculated, Read-only) */}
              {template.items_table.columns.find(col => col.name === 'Subtotal' && col.visible) && (
                <TableCell align="right">
                  <Typography>
                    {formatCurrency((item.quantity || 1) * (item.unitPrice || 0))}
                  </Typography>
                </TableCell>
              )}
              
              {/* Column 7: CGST% */}
              {template.items_table.columns.find(col => col.name === 'CGST%' && col.visible) && (
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={item.cgst}
                    onChange={(e) => handleItemChange(index, 'cgst', parseFloat(e.target.value))}
                    sx={{ width: 70 }}
                    InputProps={{
                      endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span>
                    }}
                  />
                </TableCell>
              )}
              
              {/* Column 8: SGST% */}
              {template.items_table.columns.find(col => col.name === 'SGST%' && col.visible) && (
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={item.sgst}
                    onChange={(e) => handleItemChange(index, 'sgst', parseFloat(e.target.value))}
                    sx={{ width: 70 }}
                    InputProps={{
                      endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span>
                    }}
                  />
                </TableCell>
              )}
              
              {/* Column 9: Amount (inc. GST) - Calculated, Read-only */}
              {template.items_table.columns.find(col => col.name === 'Amt (inc. GST)' && col.visible) && (
                <TableCell align="right">
                  <Typography fontWeight="bold">
                    {formatCurrency(
                      (item.quantity || 1) * (item.unitPrice || 0) *
                      (1 + (item.cgst || 0)/100 + (item.sgst || 0)/100)
                    )}
                  </Typography>
                </TableCell>
              )}
              
              {/* Column 10: Remarks */}
              {template.items_table.columns.find(col => col.name === 'Remarks' && col.visible) && (
                <TableCell>
                  <TextField
                    size="small"
                    value={item.remarks}
                    onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                    placeholder="Remarks..."
                    fullWidth
                  />
                </TableCell>
              )}
              
              {/* Actions Column (Always visible) */}
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={() => handleRemoveItem(index)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    
    <Button
      startIcon={<Add />}
      onClick={handleAddItem}
      sx={{ mt: 2 }}
    >
      Add Item
    </Button>
  </Box>
)}
        
        {/* Additional Costs Section */}
        {template?.additional_costs?.enabled && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoney /> Additional Costs
            </Typography>
            
            {quotation.additionalCosts.map((cost, index) => (
              <Box key={cost.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cost.enabled}
                      onChange={(e) => handleAdditionalCostChange(index, 'enabled', e.target.checked)}
                    />
                  }
                  label={cost.name}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  type="number"
                  label="Amount"
                  value={cost.amount}
                  onChange={(e) => handleAdditionalCostChange(index, 'amount', parseFloat(e.target.value))}
                  sx={{ width: 150 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {/* <AttachMoney /> */}
                      </InputAdornment>
                    ),
                  }}
                />
                
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedCosts = quotation.additionalCosts.filter((_, i) => i !== index);
                    setQuotation(prev => ({ ...prev, additionalCosts: updatedCosts }));
                  }}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2">
                Additional Costs Total: <strong>{formatCurrency(totals.additionalCostsTotal)}</strong>
              </Typography>
            </Paper>
          </Box>
        )}
        
{/* Summary Section */}
<Box sx={{ mb: 4, p: 3, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Receipt /> Quotation Summary
  </Typography>
  
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Items Total:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.itemsTotal)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Additional Costs:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.additionalCostsTotal)}</Typography>
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Subtotal:</Typography>
        <Typography variant="subtitle1" fontWeight="bold">{formatCurrency(totals.subtotal)}</Typography>
      </Box>
      
      {/* DISCOUNT SECTION - Only show if enabled in template */}
      {template?.summary_section?.show_discount && (
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'primary.light', borderRadius: 1, bgcolor: '#f8f9fa' }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            Discount
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              type="number"
              label="Discount %"
              size="small"
              value={quotation.discountPercent || template?.summary_section?.default_discount_percent || 0}
              onChange={(e) => setQuotation(prev => ({
                ...prev,
                discountPercent: parseFloat(e.target.value)
              }))}
              sx={{ width: 120 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Amount: <strong>{formatCurrency(totals.discountAmount)}</strong>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography>After Discount:</Typography>
            <Typography fontWeight="bold" color="success.main">
              {formatCurrency(totals.afterDiscount)}
            </Typography>
          </Box>
        </Box>
      )}
    </Grid>
    
    <Grid item xs={12} md={6}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, bgcolor: '#e8f4fd', p: 1, borderRadius: 1 }}>
        <Typography>CGST:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.cgstTotal)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, bgcolor: '#e8f4fd', p: 1, borderRadius: 1 }}>
        <Typography>SGST:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.sgstTotal)}</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, bgcolor: '#f0f0f0', p: 1, borderRadius: 1 }}>
        <Typography>Total GST:</Typography>
        <Typography fontWeight="bold">{formatCurrency(totals.gstTotal)}</Typography>
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.light', p: 2, borderRadius: 1 }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          GRAND TOTAL
        </Typography>
        <Typography variant="h5" fontWeight="bold" color="primary">
          {formatCurrency(totals.grandTotal)}
        </Typography>
      </Box>
    </Grid>
  </Grid>
  
  {/* ADVANCE PAYMENT OPTIONS SECTION - Only show if enabled in template */}
  {template?.summary_section?.show_advance_payment && (
    <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Payment Options
      </Typography>
      
      <Grid container spacing={2}>
        {/* Full Payment Option */}
        {template.summary_section.advance_payment_options?.full_payment?.enabled && (
          <Grid item xs={12} md={4}>
            <Card 
              variant={quotation.selectedPaymentOption === 'full' ? "outlined" : "elevation"}
              sx={{ 
                p: 2, 
                cursor: 'pointer',
                borderColor: quotation.selectedPaymentOption === 'full' ? 'primary.main' : 'transparent',
                bgcolor: quotation.selectedPaymentOption === 'full' ? '#e8f4fd' : 'white'
              }}
              onClick={() => handlePaymentOptionChange('full')}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Full Payment
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {template.summary_section.advance_payment_options.full_payment.description}
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {formatCurrency(totals.grandTotal)}
              </Typography>
            </Card>
          </Grid>
        )}
        
        {/* Advance Payment Option */}
        {template.summary_section.advance_payment_options?.advance_payment?.enabled && (
          <Grid item xs={12} md={4}>
            <Card 
              variant={quotation.selectedPaymentOption === 'advance' ? "outlined" : "elevation"}
              sx={{ 
                p: 2, 
                cursor: 'pointer',
                borderColor: quotation.selectedPaymentOption === 'advance' ? 'primary.main' : 'transparent',
                bgcolor: quotation.selectedPaymentOption === 'advance' ? '#e8f4fd' : 'white'
              }}
              onClick={() => handlePaymentOptionChange('advance')}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Advance Payment
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {template.summary_section.advance_payment_options.advance_payment.description}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  type="number"
                  size="small"
                  value={quotation.advancePercentage}
                  onChange={(e) => handleAdvancePercentageChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ width: 80 }}
                  InputProps={{
                    endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span>
                  }}
                />
                <Typography variant="body2">advance</Typography>
              </Box>
              
              <Typography variant="body2">
                Advance Amount: <strong>{formatCurrency(totals.advanceAmount)}</strong>
              </Typography>
              <Typography variant="body2">
                Remaining: <strong>{formatCurrency(totals.remainingAmount)}</strong>
              </Typography>
            </Card>
          </Grid>
        )}
        
        {/* Custom Payment Option */}
        {template.summary_section.advance_payment_options?.custom_payment?.enabled && (
          <Grid item xs={12} md={4}>
            <Card 
              variant={quotation.selectedPaymentOption === 'custom' ? "outlined" : "elevation"}
              sx={{ 
                p: 2, 
                cursor: 'pointer',
                borderColor: quotation.selectedPaymentOption === 'custom' ? 'primary.main' : 'transparent',
                bgcolor: quotation.selectedPaymentOption === 'custom' ? '#e8f4fd' : 'white'
              }}
              onClick={() => handlePaymentOptionChange('custom')}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Custom Payment Terms
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {template.summary_section.advance_payment_options.custom_payment.description}
              </Typography>
              <TextField
                size="small"
                placeholder="Enter custom terms..."
                fullWidth
                sx={{ mt: 1 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )}
  
  {/* BANK DETAILS SECTION - Only show if enabled in template */}
  {template?.summary_section?.show_bank_details && quotation.selectedPaymentOption !== 'custom' && (
    <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#f5f5f5' }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Bank Account Details for Payment
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Bank Name"
            size="small"
            fullWidth
            value={quotation.bankDetails?.bank_name || ''}
            onChange={(e) => handleBankDetailChange('bank_name', e.target.value)}
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Account Name"
            size="small"
            fullWidth
            value={quotation.bankDetails?.account_name || ''}
            onChange={(e) => handleBankDetailChange('account_name', e.target.value)}
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Account Number"
            size="small"
            fullWidth
            value={quotation.bankDetails?.account_number || ''}
            onChange={(e) => handleBankDetailChange('account_number', e.target.value)}
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="IFSC Code"
            size="small"
            fullWidth
            value={quotation.bankDetails?.ifsc_code || ''}
            onChange={(e) => handleBankDetailChange('ifsc_code', e.target.value)}
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Branch"
            size="small"
            fullWidth
            value={quotation.bankDetails?.branch || ''}
            onChange={(e) => handleBankDetailChange('branch', e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  )}
  
  {/* NOTES BOX SECTION - Only show if enabled in template */}
  {template?.summary_section?.show_notes_box && (
    <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff8e1' }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Notes
      </Typography>
      <TextField
        multiline
        rows={3}
        fullWidth
        value={template.summary_section.notes_content}
        onChange={(e) => {
          // Update template notes content
          const updatedTemplate = { ...template };
          updatedTemplate.summary_section.notes_content = e.target.value;
          setTemplate(updatedTemplate);
        }}
        sx={{ bgcolor: 'white' }}
      />
    </Box>
  )}
</Box>
        {/* Terms & Conditions */}
        {template?.terms_section?.enabled && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocalShipping /> Terms & Conditions
            </Typography>
            
            {quotation.terms
              .filter(term => term.visible)
              .map((term, index) => (
                <Box key={term.id} sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label={term.label}
                    multiline
                    rows={2}
                    value={term.content}
                    onChange={(e) => {
                      const updatedTerms = [...quotation.terms];
                      updatedTerms[index].content = e.target.value;
                      setQuotation(prev => ({ ...prev, terms: updatedTerms }));
                    }}
                  />
                </Box>
              ))}
          </Box>
        )}
      </Paper>
      
      {/* Action Buttons at Bottom */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/template-designer')}
        >
          Back to Designer
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
      
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSaveQuotation}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Quotation'}
          </Button>
        </Box>
      </Box>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LiveQuotationFromTemplate;